import { BRAND } from '@/lib/const';
import type {
  WithdrawalEligibility,
  WithdrawalLineStatus,
} from '@/lib/withdrawal/types';
import { normalizeWithdrawalCode } from '@/lib/withdrawal/withdrawalCode';

const WITHDRAWAL_LINE_STATUSES = new Set<string>(['ELIGIBLE', 'INELIGIBLE']);

function toWithdrawalLineStatus(status: string): WithdrawalLineStatus {
  return WITHDRAWAL_LINE_STATUSES.has(status)
    ? (status as WithdrawalLineStatus)
    : 'INELIGIBLE';
}

type GuestLookupInput = {
  businessId: string;
  email: string;
  recaptchaToken: string;
};

type WithdrawalEligibilityApiResponse = {
  data?: {
    withdrawalEligibility?: {
      errors?: Array<{ code: string; message: string }>;
      withdrawal?: {
        code: string;
        lines: Array<{
          disabledReasonCode?: string | null;
          eligibleQuantity: number;
          orderLineId: string;
          quantity: number;
          status: string;
          windowEndAt?: string | null;
        }>;
      } | null;
    };
  };
};

export const fetchGuestWithdrawalEligibility = async (
  input: GuestLookupInput,
): Promise<WithdrawalEligibility | null> => {
  const response = await fetch('/api/withdrawal/eligibility', {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({
      input: {
        brand: BRAND.toUpperCase(),
        guestLookup: input,
      },
    }),
  });

  if (!response.ok) {
    return null;
  }

  const payload = (await response.json()) as WithdrawalEligibilityApiResponse;
  const withdrawal = payload.data?.withdrawalEligibility?.withdrawal;
  const firstError = payload.data?.withdrawalEligibility?.errors?.[0];

  if (withdrawal) {
    return {
      code: normalizeWithdrawalCode(withdrawal.code) ?? 'VALIDATION_ERROR',
      lines: withdrawal.lines.map((line) => ({
        orderLineId: line.orderLineId,
        quantity: line.quantity,
        eligibleQuantity: line.eligibleQuantity,
        status: toWithdrawalLineStatus(line.status),
        disabledReasonCode: line.disabledReasonCode ?? null,
        windowEndAt: line.windowEndAt ?? null,
      })),
    };
  }

  if (firstError?.code) {
    return {
      code: normalizeWithdrawalCode(firstError.code) ?? 'VALIDATION_ERROR',
      lines: [],
    };
  }

  return null;
};
